import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { Disposition, BidRound, DispositionGroup } from '../types/dispositions.types';
import { AccountService } from 'app/core';

@Injectable({
  providedIn: 'root',
})
export class DispositionsService {
  private _dispositionGroups: BehaviorSubject<Disposition[] | null>;
  private _dispositions: BehaviorSubject<Disposition[] | null>;
  private _disposition: BehaviorSubject<Disposition | null>;
  selectedNoteChange: BehaviorSubject<any>;

  private _projectId: string;
  private _accountId: string;

  constructor(
    private _httpClient: HttpClient,
    private _accountService: AccountService,
  ) {
    this._dispositions = new BehaviorSubject(null);
    this._disposition = new BehaviorSubject(null);
    this._dispositionGroups = new BehaviorSubject(null);
    this._accountService.currentAccount.subscribe((accountData) => {
      this._accountId = accountData.id;
    });
  }

  get dispositions$(): Observable<Disposition[]> {
    return this._dispositions.asObservable();
  }

  get disposition$(): Observable<Disposition> {
    return this._disposition.asObservable();
  }

  get dispositionGroups$(): Observable<any> {
    return this._dispositionGroups.asObservable();
  }

  set accountId(val: string) {
    this._accountId = val;
  }

  set projectId(val: string) {
    this._projectId = val;
  }

  get projectId() {
    return this._projectId;
  }

  getDispositionById(id: number, group): Observable<Disposition> {
    return this._httpClient
      .get<any>(`projects/${this.projectId}/disposition_groups/${group.id}/disposition_group_transactions/${id}`)
      .pipe(
        map((response) => {
          this._disposition.next(response.offer);
          return response.offer;
        }),
      );
  }

  createDisposition(disposition: Disposition, group): Observable<Disposition> {
    return this.dispositions$.pipe(
      take(1),
      switchMap((dispositions) =>
        this._httpClient
          .post<any>(`projects/${this.projectId}/disposition_groups/${group.id}/disposition_group_transactions`, {
            disposition_group_transaction: disposition,
          })
          .pipe(
            map((response) => {
              let groups = this.getDispositionGroups().subscribe();

              return response;
            }),
          ),
      ),
    );
  }

  updateDisposition(disposition: Disposition, group): Observable<Disposition> {
    return this.dispositions$.pipe(
      take(1),
      switchMap((dispositions) =>
        this._httpClient
          .put<any>(
            `projects/${this.projectId}/disposition_groups/${group.id}/disposition_group_transactions/${disposition.id}`,
            { disposition_group_transaction: disposition },
          )
          .pipe(
            map((response) => {
              let groups = this.getDispositionGroups().subscribe();

              return response;
            }),
          ),
      ),
    );
  }

  deleteDisposition(id: number, group): Observable<any> {
    return this.dispositions$.pipe(
      take(1),
      switchMap((dispositions) =>
        this._httpClient
          .delete<any>(
            `projects/${this.projectId}//disposition_groups/${group.id}/disposition_group_transactions/${id}`,
          )
          .pipe(
            map((response) => {
              let groups = this.getDispositionGroups().subscribe();

              return response;
            }),
          ),
      ),
    );
  }

  createDispositionGroup(dispositionGroup: any): Observable<any> {
    return this.dispositionGroups$.pipe(
      take(1),
      switchMap((dispositions) =>
        this._httpClient
          .post<any>(`projects/${this.projectId}/disposition_groups`, {
            disposition_group: dispositionGroup,
          })
          .pipe(
            map((response) => {
              let groups = this.getDispositionGroups().subscribe();

              return response;
            }),
          ),
      ),
    );
  }

  updateDispositionGroup(dispositionGroup: any): Observable<any> {
    return this.dispositionGroups$.pipe(
      take(1),
      switchMap((dispositions) =>
        this._httpClient
          .put<any>(`projects/${this.projectId}/disposition_groups/${dispositionGroup.id}`, {
            disposition_group: dispositionGroup,
          })
          .pipe(
            map((response) => {
              let groups = this.getDispositionGroups().subscribe();

              return response;
            }),
          ),
      ),
    );
  }

  deleteDispositionGroup(id: any): Observable<any> {
    return this.dispositionGroups$.pipe(
      take(1),
      switchMap((dispositionGroup) =>
        this._httpClient.delete<any>(`projects/${this.projectId}/disposition_groups/${id}`).pipe(
          map((response) => {
            let groups = this.getDispositionGroups().subscribe();

            return response;
          }),
        ),
      ),
    );
  }

  getDispositionGroups(): Observable<{ disposition_groups: DispositionGroup[] }> {
    return this._httpClient.get<any>(`projects/${this.projectId}/disposition_groups`).pipe(
      tap((response: any) => {
        this._dispositionGroups.next(response);
      }),
    );
  }

  uploadDocument(disposition: Disposition, document: any): Observable<Disposition> {
    const uploadData = new FormData();
    uploadData.append('document[attachment]', document);

    return this.dispositions$.pipe(
      take(1),
      switchMap((dispositions) =>
        this._httpClient
          .post<any>(`projects/${this.projectId}/offers/${disposition.id}/upload_document`, uploadData)
          .pipe(
            map((response) => {
              let updatedOffer = response.offer;

              const index = dispositions.findIndex((item) => item.id === disposition.id);

              dispositions[index] = updatedOffer;

              this._dispositions.next(dispositions);
              this._disposition.next(updatedOffer);

              return updatedOffer;
            }),
          ),
      ),
    );
  }

  getBidRounds(): Observable<BidRound[]> {
    let offerRounds = [
      {
        key: 'Round 1',
        label: 'Round 1',
      },
      {
        key: 'Round 2',
        label: 'Round 2',
      },
      {
        key: 'Round 3',
        label: 'Round 3',
      },
      {
        key: 'Best & Final',
        label: 'Best & Final',
      },
    ];

    return of(offerRounds);
  }
}
